import type {
    IPhoto,
    IPhotoCropSuggestions,
    ISurface,
    IContainer,
    IAsset,
    IPhotoCrop,
    IUserPhotoAsset,
    CropType,
} from '../common/types/index.js';
import { cropPhotoToContainer } from '../crop/cropPhotoToContainer.js';

type Orientation = 'landscape' | 'portrait' | 'square';

function getOrientation(width: number, height: number): Orientation {
    if (width > height) {
        return 'landscape';
    }

    if (height > width) {
        return 'portrait';
    }

    return 'square';
}

function autofillSurface(surface: ISurface, photos: IPhoto[], cropStrategy?: CropType): ISurface {
    if (photos.length === 0) {
        return surface;
    }

    const rankedAssets = sortContainersBySize(surface.assets);
    const photosCrops = cropPhotos(photos, rankedAssets, cropStrategy);
    const scoredCrops = scoreCrops(photosCrops);
    const filledContainers = matchPhotosToContainers(rankedAssets, scoredCrops);

    const updatedAssets = surface.assets
        .map((asset: IAsset) => {
            return filledContainers.find((c) => c.id === asset.id) || asset;
        });

    return { ...surface, assets: updatedAssets };
}

function sortContainersBySize(assets: IAsset[]): IAsset[] {
    return [...assets]
        .sort((a, b) => (b.container.width * b.container.height) - (a.container.width * a.container.height));
}

function cropPhotos(photos: IPhoto[], rankedAssets: IAsset[], cropStrategy?: CropType): IPhotoCropSuggestions[] {
    return photos.map((photo: IPhoto) => {
        const photoContainerCrops: { asset: IAsset ; crop: IPhotoCrop }[] = rankedAssets
            .map((asset: IAsset) => {
                return { asset, crop: cropPhotoToContainer(photo, asset.container, cropStrategy) };
            });

        return { photo, possibleCrops: photoContainerCrops };
    });
}

// score crops based on photo rank and crop quality
function scoreCrops(croppedPhotos: IPhotoCropSuggestions[]): IPhotoCropSuggestions[] {
    return croppedPhotos.map((photoCrop) => ({
        photo: photoCrop.photo,
        possibleCrops: photoCrop.possibleCrops.map((pCrop) => ({
            ...pCrop,
            score: evaluateCrop(pCrop.crop, photoCrop.photo, pCrop.asset.container),
        })),
    }));
}

function evaluateCrop(crop: IPhotoCrop, photo: IPhoto, container: IContainer): number {
    const photoRankFactor = 0.4;
    const photoCropQFactor = 0.4;
    const orientationFactor = 0.2;
    const normalizedRank = photo.rank ?? 0;
    const cropRank = crop.rank;
    const orientationMatch = getOrientation(photo.width, photo.height) === getOrientation(container.width, container.height) ? 1 : 0;

    return (normalizedRank * photoRankFactor) + (cropRank * photoCropQFactor) + (orientationMatch * orientationFactor);
}

function matchPhotosToContainers(rankedAssets: IAsset[], photos: IPhotoCropSuggestions[]): IUserPhotoAsset[] {
    let remainingPhotos = [...photos];

    return rankedAssets.map((asset, assetRank) => {
        if (remainingPhotos.length === 0) {
            return { ...asset } as IUserPhotoAsset;
        }

        const { bestMatch, updatedPhotoCrops } = getBestMatch(assetRank, remainingPhotos);

        remainingPhotos = updatedPhotoCrops;

        return {
            ...asset,
            type: 'UserPhoto',
            content: {
                photoId: bestMatch.photo.photoId,
                source: bestMatch.photo.source,
                crop: bestMatch.crop.crop,
            },
        } as IUserPhotoAsset;
    });
}

function getBestMatch(assetRank: number, photoCrops: IPhotoCropSuggestions[]): { bestMatch: { crop: { asset: IAsset; crop: IContainer; score?: number }; photo: IPhoto } ; updatedPhotoCrops: IPhotoCropSuggestions[] } {
    const sortedCrops = photoCrops.map((photoCrop) => ({
        photo: photoCrop.photo,
        crop: photoCrop.possibleCrops[assetRank],
    })).sort((a, b) => (b.crop.score ?? 0) - (a.crop.score ?? 0));

    const bestMatch = sortedCrops.shift();

    if (!bestMatch) {
        throw new Error('No valid match found in crops for the given asset.');
    }

    return {
        bestMatch: bestMatch,
        updatedPhotoCrops: photoCrops.filter((photoCrop) => photoCrop.photo.photoId !== bestMatch.photo.photoId),
    };
}

export { autofillSurface };
